import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, Alert, Badge } from 'react-bootstrap';
import api from '../api';

function Locations({ user }) {
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    clientId: '',
    name: '',
    address: '',
    city: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLocations();
    if (user.role === 'super_admin') {
      fetchClients();
    }
  }, [user.role]);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/api/locations');
      setLocations(response.data.data);
    } catch (err) {
      setError('Σφάλμα κατά τη φόρτωση τοποθεσιών');
      console.error(err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/clients');
      setClients(response.data.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleShowModal = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        clientId: location.clientId,
        name: location.name,
        address: location.address || '',
        city: location.city || '',
        description: location.description || ''
      });
    } else {
      setEditingLocation(null);
      setFormData({
        clientId: user.role === 'client_admin' ? user.clientId : '',
        name: '',
        address: '',
        city: '',
        description: ''
      });
    }
    setShowModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLocation(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingLocation) {
        await api.put(`/api/locations/${editingLocation.id}`, formData);
        setSuccess('Η τοποθεσία ενημερώθηκε επιτυχώς');
      } else {
        await api.post('/api/locations', formData);
        setSuccess('Η τοποθεσία δημιουργήθηκε επιτυχώς');
      }
      handleCloseModal();
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Σφάλμα κατά την αποθήκευση');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την τοποθεσία; Θα διαγραφούν και όλες οι συσκευές της.')) {
      return;
    }

    try {
      await api.delete(`/api/locations/${id}`);
      setSuccess('Η τοποθεσία διαγράφηκε επιτυχώς');
      fetchLocations();
    } catch (err) {
      setError('Σφάλμα κατά τη διαγραφή');
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2><i className="bi bi-geo-alt me-2"></i>Διαχείριση Τοποθεσιών</h2>
          {user.role === 'client_admin' && user.clientName && (
            <p className="text-muted mb-0">{user.clientName}</p>
          )}
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => handleShowModal()}>
            <i className="bi bi-plus-circle me-1"></i>
            Νέα Τοποθεσία
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                {user.role === 'super_admin' && <th>Client</th>}
                <th>Όνομα</th>
                <th>Πόλη</th>
                <th>Διεύθυνση</th>
                <th>Συσκευές</th>
                <th>Κατάσταση</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {locations.map(location => (
                <tr key={location.id}>
                  <td>{location.id}</td>
                  {user.role === 'super_admin' && (
                    <td>{location.Client?.name || '-'}</td>
                  )}
                  <td><strong>{location.name}</strong></td>
                  <td>{location.city || '-'}</td>
                  <td>{location.address || '-'}</td>
                  <td>
                    <Badge bg="info">{location.Devices?.length || 0}</Badge>
                  </td>
                  <td>
                    <Badge bg={location.isActive ? 'success' : 'secondary'}>
                      {location.isActive ? 'Ενεργή' : 'Ανενεργή'}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button 
                        size="sm" 
                        variant="outline-primary"
                        onClick={() => handleShowModal(location)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline-danger"
                        onClick={() => handleDelete(location.id)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {locations.length === 0 && (
            <div className="text-center py-4 text-muted">
              Δεν υπάρχουν τοποθεσίες. Πατήστε "Νέα Τοποθεσία" για να δημιουργήσετε.
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingLocation ? 'Επεξεργασία Τοποθεσίας' : 'Νέα Τοποθεσία'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            
            {user.role === 'super_admin' && !editingLocation && (
              <Form.Group className="mb-3">
                <Form.Label>Client *</Form.Label>
                <Form.Select
                  value={formData.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                  required
                >
                  <option value="">Επιλέξτε Client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Όνομα Τοποθεσίας *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="π.χ. Κεντρικό Κατάστημα"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Πόλη</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="π.χ. Θεσσαλονίκη"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Διεύθυνση</Form.Label>
              <Form.Control
                type="text"
                placeholder="π.χ. Τσιμισκή 100, 54622"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Περιγραφή</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Προαιρετική περιγραφή..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Ακύρωση
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Locations;
